// src/app/login/page.tsx
'use client';

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Alert
} from '@mui/material';
/**
 * Validate a next parameter before redirecting to it.
 *
 * Anything that is not same-origin is rejected outright, so a crafted
 * /login?next=https://evil.com cannot turn this page into an open
 * redirect. Parsing against the current origin rather than pattern
 * matching on the string also covers //evil.com and the backslash form
 * /\evil.com, which the URL parser folds to a slash and a naive
 * startsWith('/') check would let through.
 *
 * Returns path and query only, so a same-origin absolute URL is
 * normalised to a relative one and any fragment is dropped. A next
 * pointing back at /login is rejected as well; the proxy never produces
 * one, but a hand-written URL would otherwise bounce the user straight
 * back to this page.
 */
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === '/login' || url.pathname.startsWith('/login/')) {
      return null;
    }
    return url.pathname + url.search;
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername, password })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? 'Authentication error');
      } else {
        // Hard navigation, not router.push. The dashboard reads its deep
        // link from window.location.search in a mount effect, and a full
        // load guarantees the URL is in place before that effect runs
        // rather than depending on when the App Router updates history
        // during a soft navigation. Read at submit time, so no mount
        // effect and no Suspense boundary is needed here.
        const next = safeNext(
          new URLSearchParams(window.location.search).get('next')
        );
        window.location.assign(next ?? '/dashboard');
      }
    } catch (err: any) {
      setError(err.message ?? 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      minHeight="100vh"
      alignItems="center"
      justifyContent="center"
    >
      <Card sx={{ minWidth: 360 }}>
        <CardHeader title="Fleet Dashboard Access" />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <form onSubmit={onSubmit}>
            <TextField
              label="Email or username"
              type="text"
              margin="normal"
              autoComplete="username"
              fullWidth
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              required
            />
            <TextField
              label="Password"
              type="password"
              margin="normal"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? 'Accesso...' : 'Accedi'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
